import React, { useState } from 'react';
import { View } from 'react-native';
import Typography from 'src/components/Text/Text';
import Button from 'src/components/Button/Button';
import Divider from 'src/components/Divider/Divider';
import AmountSelector from './AmountSelector';
import IconInput from 'src/components/IconInput/IconInput';
import InputField from 'src/components/input/input';
import RadioModal from 'src/components/RadioModal/RadioModal';
import { styles } from '../screens/Insurance/InsuranceScreen.styles';
import arrowDown from 'src/assets/arrowDown-3x.png';

const yesNoOptions = [{ label: 'Yes' }, { label: 'No' }];

interface PhysicalDamageProps {
  isProducer: boolean;
  deductible: {
    value: number;
    set: (value: number) => void;
  };
  collisionDeductible: {
    value: number;
    set: (value: number) => void;
  };
  trailerInterchangeLimit: {
    value: string;
    set: (value: string) => void;
  };
  hasTrailerInterchangeAgreement: {
    value: string;
    set: (value: string) => void;
  };
  uiiaIntermodal: {
    value: string;
    set: (value: string) => void;
  };
  towingLaborStorage: {
    value: number;
    set: (value: number) => void;
  };
}

const PhysicalDamage: React.FC<PhysicalDamageProps> = ({
  isProducer,
  deductible,
  collisionDeductible,
  trailerInterchangeLimit,
  hasTrailerInterchangeAgreement,
  uiiaIntermodal,
  towingLaborStorage,
}) => {
  const [isAgreementModalVisible, setAgreementModalVisible] = useState(false);
  const [isUiiaModalVisible, setUiiaModalVisible] = useState(false);
  const trailerLimitAmount =
    trailerInterchangeLimit.value && trailerInterchangeLimit.value !== 'opt-out'
      ? parseInt(trailerInterchangeLimit.value, 10) || 0
      : 0;
  const isLimitButtonSelected = ['opt-out', '30000', '50000'].includes(
    trailerInterchangeLimit.value,
  );
  const isTrailerInterchangeSelected = trailerLimitAmount > 0;

  return (
    <View style={styles.switchSectionsCardContents}>
      <AmountSelector
        label={isProducer ? 'Deductible Comprehensive' : 'Deductible'}
        description={
          isProducer
            ? 'What is the maximum you would like to pay for comprehensive claims?'
            : 'What is the maximum you would like to pay in case of a claim?'
        }
        presets={[1000, 2500, 5000]}
        value={deductible.value}
        onChange={deductible.set}
        testIDPrefix="insurance-physical-damage-deductible"
      />

      {isProducer && (
        <>
          <Divider />
          <AmountSelector
            label="Deductible Collision"
            description="What is the maximum you would like to pay for collision claims?"
            presets={[1000, 2500, 5000]}
            value={collisionDeductible.value}
            onChange={collisionDeductible.set}
            testIDPrefix="insurance-collision-deductible"
          />
        </>
      )}

      <Divider />
      <Typography variant="title2">Trailer Interchange Limit</Typography>
      <View style={styles.buttonGroupContainer}>
        {['opt-out', '30000', '50000'].map((value) => (
          <Button
            testID={`insurance-trailer-interchange-limit-${value}`}
            key={value}
            title={value === 'opt-out' ? 'Opt-Out' : `$${parseInt(value).toLocaleString()}`}
            type={trailerInterchangeLimit.value === value ? 'secondary' : 'tertiary'}
            onPress={() => trailerInterchangeLimit.set(value)}
            style={styles.switchButton}
          />
        ))}
      </View>
      {isProducer && trailerInterchangeLimit.value !== 'opt-out' && (
        <InputField
          testID="insurance-trailer-interchange-limit-custom"
          variant="amount"
          label={isLimitButtonSelected ? 'Or enter custom amount' : 'Custom amount'}
          value={trailerLimitAmount > 0 ? String(trailerLimitAmount) : ''}
          onChange={(text) => {
            if (text === '') {
              // InputField fires onChange on mount; only downgrade to Opt-Out when an
              // actual amount was cleared, so we never clobber the current selection.
              if (trailerLimitAmount > 0) {
                trailerInterchangeLimit.set('opt-out');
              }
              return;
            }
            const cleanedText = text.replace(/[^0-9]/g, '');
            const numeric = parseInt(cleanedText, 10);
            if (!isNaN(numeric) && numeric >= 0) {
              trailerInterchangeLimit.set(numeric > 0 ? String(numeric) : 'opt-out');
            }
          }}
        />
      )}

      {isProducer && isTrailerInterchangeSelected && (
        <>
          <IconInput
            label="Do you have a signed trailer interchange agreement?"
            testID="insurance-trailer-interchange-agreement"
            value={hasTrailerInterchangeAgreement.value}
            placeholder="Select an option"
            onPress={() => setAgreementModalVisible(true)}
            iconSource={arrowDown}
            iconHeight={6.67}
            iconWidth={16.67}
          />
          <RadioModal
            testID="insurance-trailer-interchange-agreement-modal"
            isVisible={isAgreementModalVisible}
            onSelectOption={hasTrailerInterchangeAgreement.set}
            onButtonClickLeft={() => setAgreementModalVisible(false)}
            onClose={() => setAgreementModalVisible(false)}
            title="Do you have a signed trailer interchange agreement?"
            options={yesNoOptions}
            selectedValue={hasTrailerInterchangeAgreement.value}
          />
          {hasTrailerInterchangeAgreement.value === 'No' && (
            <Typography variant="body1">
              No signed agreement — consider Non-Owned Trailer Physical Damage instead.
            </Typography>
          )}
          <IconInput
            label="UIIA - Intermodal"
            testID="insurance-uiia-intermodal"
            value={uiiaIntermodal.value}
            placeholder="Select an option"
            onPress={() => setUiiaModalVisible(true)}
            iconSource={arrowDown}
            iconHeight={6.67}
            iconWidth={16.67}
          />
          <RadioModal
            testID="insurance-uiia-intermodal-modal"
            isVisible={isUiiaModalVisible}
            onSelectOption={uiiaIntermodal.set}
            onButtonClickLeft={() => setUiiaModalVisible(false)}
            onClose={() => setUiiaModalVisible(false)}
            title="UIIA - Intermodal"
            options={yesNoOptions}
            selectedValue={uiiaIntermodal.value}
          />
        </>
      )}

      <AmountSelector
        label="Towing Labor and Storage"
        presets={[2500, 5000, 10000, 15000]}
        value={towingLaborStorage.value}
        onChange={towingLaborStorage.set}
        testIDPrefix="towing-labor-storage"
      />
    </View>
  );
};

export default PhysicalDamage;
